import { Controller, Form, FormProvider } from 'react-hook-form';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useEditProfile } from './hooks';
import { t } from 'i18next';
import {
  DatepickerFormField,
  RespitePartnersSearchFormField,
  SelectFormField,
  TextFormField,
} from 'components/DynamicFormFields';
import { CustomModal, SaveChangesModal, StatusBanner } from '@components';
import { theme } from '@store';
import { StatusChangeConfirmationModal } from '@views/pages/Patient/components/DetailsCard/components/StatusChangeConfirmationModal';
import dayjs from 'dayjs';
import { isNil } from 'lodash';

interface IEditProfileProps {
  patient_id: string;
  disabled: boolean;
  patient_name: string;
  patient_birthdate: string;
}

export const EditProfile = ({
  patient_id,
  disabled = false,
  patient_name,
  patient_birthdate,
}: IEditProfileProps) => {
  const {
    formMethods,
    handleClose,
    handleOnClick,
    isOpen,
    isSubmitting,
    onSubmit,
    referralSourceOptions,
    sourceDetailOptions,
    ineligibilityReasons,
    accountStatusOptions,
    genderOptions,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
    reasonInactiveStatusOptions,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    showWarningBanner,
    modalHeight,
  } = useEditProfile(patient_id);

  const { control, watch } = formMethods;
  const accountStatus = watch('account_status');
  const referralSource = watch('referral_source');
  const inactivity_date = watch('inactivity_date');
  const respite_eligible = watch('respite_eligible');
  const approximate = watch('inactivity_date_approximate');

  return (
    <>
      <Button
        disabled={disabled}
        onClick={handleOnClick}
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          minWidth: 'unset',
        }}
      >
        EDIT
      </Button>
      <CustomModal
        isOpen={isOpen && !disabled}
        onClose={handleClose}
        isLoading={isSubmitting}
        height={modalHeight}
        title={t('patient.detailsCard.editPatientProfile.title')}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {showWarningBanner && (
              <StatusBanner
                text={t('patient.detailsCard.editPatientInfo.warning')}
                variant='warning'
              />
            )}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <TextFormField
                name='first_name'
                label={t('patients.addModal.firstName')}
              />

              <TextFormField
                name='middle_name'
                label={t('patients.addModal.middleName')}
                optional
              />
            </Box>
            <TextFormField
              name='last_name'
              label={t('patients.addModal.lastName')}
            />
            <TextFormField
              name='preferred_name'
              label={t('patients.addModal.preferredName')}
              optional
            />
            <Typography
              variant='caption'
              sx={{ color: 'text.disabled', px: 1, mt: '-16px' }}
            >
              {t('patients.addModal.preferredNameHelperText')}
            </Typography>
            <DatepickerFormField
              name='birthdate'
              label={t('patients.addModal.birthdate')}
              optional={false}
            />
            <SelectFormField
              name='gender'
              label={t('patients.addModal.gender.fieldLabel')}
              options={genderOptions}
            />
            <SelectFormField
              name='referral_source'
              label={'Referral source'}
              options={referralSourceOptions}
            />
            <SelectFormField
              name='source_detail'
              label={'Source detail (optional)'}
              options={sourceDetailOptions}
              optional={true}
            />
            <Typography variant='body1' component='h2'>
              {t('patient.detailsCard.editPatientProgram.respiteServicesLabel')}
            </Typography>
            <Controller
              name={'respite_eligible'}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl sx={{ width: '100%' }}>
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      key={'Unknown option'}
                      value={'Unknown'}
                      control={<Radio value={'Unknown'} />}
                      label={t(
                        'patient.detailsCard.editPatientProgram.unknown',
                      )}
                    />
                    <FormControlLabel
                      key={'Eligible option'}
                      value={'Eligible'}
                      control={<Radio value={'Eligible'} />}
                      label={t(
                        'patient.detailsCard.editPatientProgram.eligible',
                      )}
                    />
                    <FormControlLabel
                      key={'Ineligible option'}
                      value={'Ineligible'}
                      control={<Radio value={'Ineligible'} />}
                      label={t(
                        'patient.detailsCard.editPatientProgram.ineligible',
                      )}
                    />
                  </RadioGroup>
                  <FormHelperText error={!isNil(error)}>
                    {error ? error.message : null}
                  </FormHelperText>
                </FormControl>
              )}
            />
            {respite_eligible === 'Ineligible' && (
              <SelectFormField
                name='respite_ineligibility_reason'
                label={'Ineligibility reason'}
                options={ineligibilityReasons}
                optional={false}
              />
            )}
            {(referralSource === 'Respite partner' ||
              respite_eligible === 'Eligible') && (
              <RespitePartnersSearchFormField
                name='respite_partner_id'
                placeholder={t(
                  'patient.detailsCard.editPatientProgram.respitePartner',
                  {
                    required:
                      referralSource === 'Respite partner' ? '' : ' (optional)',
                  },
                )}
                optional={referralSource !== 'Respite partner'}
                patient_id={patient_id}
              />
            )}
            <SelectFormField
              name='account_status'
              label={'Account status'}
              options={accountStatusOptions}
            />

            {accountStatus === 'Inactive' && (
              <>
                <Typography
                  variant='body2'
                  sx={{ m: '-12px 8px 0 8px', color: 'text.disabled' }}
                >
                  {t('patient.detailsCard.editPatientInfo.inactiveWarning')}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <SelectFormField
                    name='inactivity_reason'
                    label={t('patient.detailsCard.editPatientInfo.reason')}
                    options={reasonInactiveStatusOptions}
                  />

                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <DatepickerFormField
                      name='inactivity_date'
                      label={t('patient.detailsCard.editPatientInfo.date')}
                      optional={false}
                      sx={{
                        '& .MuiPickersInputBase-sectionsContainer': {
                          paddingLeft: approximate ? '16px' : '0px',
                        },
                      }}
                    />
                    {approximate && inactivity_date && (
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: '15px',
                          left: '16px',
                        }}
                      >
                        ~
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    m: '-16px 100px 0 0',
                  }}
                >
                  <Controller
                    control={control}
                    name='inactivity_date_approximate'
                    defaultValue={false}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox checked={value} onChange={onChange} />
                        }
                        label={t(
                          'patient.detailsCard.editPatientInfo.approximate',
                        )}
                      />
                    )}
                  />
                </Box>
              </>
            )}

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting || isEditSubmitting || !isValid}
                loading={isEditSubmitting}
                loadingPosition='start'
              >
                {t(`general.${isEditSubmitting ? 'saving' : 'save'}`)}
              </Button>
            </Box>
            <SaveChangesModal
              isOpen={isSaveChangesModalOpen}
              onClose={handleCloseSaveChangesModal}
              isValid={isValid}
              handleSaveChanges={handleSaveChanges}
              discardChanges={handleDiscardChanges}
              isLoading={isEditSubmitting}
            />
            <StatusChangeConfirmationModal
              name={patient_name}
              date={dayjs(patient_birthdate).format('MMM DD, YYYY')}
              isOpen={isConfirmModalOpen}
              onClose={() => setIsConfirmModalOpen(false)}
              handleSaveChanges={handleSaveChanges}
              isLoading={isEditSubmitting}
            />
          </Form>
        </FormProvider>
      </CustomModal>
    </>
  );
};
